import React, { useEffect, useState, useMemo } from "react";
import { Button, Form, Dropdown } from "react-bootstrap";
import { startOfMonth, endOfMonth, subMonths, format } from "date-fns";
import { FaCog } from "react-icons/fa";
import useFetchDatav2 from "./useFetchDatav2";
import ReportSummaryStrip from "./ReportSummaryStrip";
import { reports } from "../reports";
import { rowsToCsv, downloadCsv } from "../utils/reportCsv";


// ── Formatters ────────────────────────────────────────────────────────────────
const money = (v) =>
   v == null
      ? ""
      : v < 0
      ? `(${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
      : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const cell = (col, row) => {
   const v = row[col.key];
   if (col.type === "money") {
      if (v == null) return <span style={{ color: "#6c757d" }}>—</span>;
      const neg = Number(v) < 0;
      return (
         <span
            style={{
               fontVariantNumeric: "tabular-nums",
               color: col.accent && neg ? "#dc3545" : undefined,
            }}
         >
            {money(Number(v))}
         </span>
      );
   }
   return v ?? "";
};

const mkDrillCell = (openRow, setOpenRow) => (col, row, rowId) => {
   if (col.drill === "pair") {
      return (
         <a
            href="#"
            style={{ color: "#0073bb" }}
            onClick={(e) => {
               e.preventDefault();
               setOpenRow(openRow === rowId ? null : rowId);
            }}
         >
            {cell(col, row)}
         </a>
      );
   }
   return cell(col, row);
};

// ── Sort comparator ───────────────────────────────────────────────────────────
// Nulls go last; handles numbers, ISO date strings, other strings.
function compareBy(key, dir) {
   return (a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp;
      if (typeof av === "number" && typeof bv === "number") {
         cmp = av - bv;
      } else {
         cmp = String(av).localeCompare(String(bv));
      }
      return dir === "desc" ? -cmp : cmp;
   };
}

// ── Group rows by a field, computing subtotals for sum-total columns ──────────
function groupRows(rows, field, sortKey, sortDir, columns) {
   const sumCols = (columns || []).filter((c) => c.total === "sum").map((c) => c.key);
   const map = new Map();
   for (const row of rows) {
      const k = row[field] ?? "(blank)";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(row);
   }
   return Array.from(map.entries()).map(([key, groupRows_]) => {
      const sorted =
         sortKey
            ? [...groupRows_].sort(compareBy(sortKey, sortDir))
            : groupRows_;
      const subtotals = {};
      for (const col of sumCols) {
         subtotals[col] = sorted.reduce((s, r) => s + (r[col] ?? 0), 0);
      }
      return { key, label: key, rows: sorted, subtotals };
   });
}

// ── Compute summary from flat row list, driven by def.summary agg specs ───────
function computeSummary(rows, summaryDefs) {
   return summaryDefs.map((spec) => {
      let value;
      switch (spec.agg) {
         case "count":
            value = rows.length;
            break;
         case "sum":
            value = rows.reduce((s, r) => s + (r[spec.column] ?? 0), 0);
            break;
         case "countWhere":
            value = rows.filter((r) => r[spec.column] === spec.equals).length;
            break;
         case "sumExposure":
            value = rows.reduce(
               (s, r) => s + Math.abs(r.variance ?? r.statement_amount ?? r.erp_amount ?? 0),
               0
            );
            break;
         default:
            value = 0;
      }
      return { key: spec.key, label: spec.label, type: spec.type, value };
   });
}

// ── Compute totals (sum columns) from flat row list ───────────────────────────
function computeTotals(rows, columns) {
   const totals = {};
   for (const col of columns) {
      if (col.total === "sum") {
         totals[col.key] = rows.reduce((s, r) => s + (r[col.key] ?? 0), 0);
      }
   }
   return totals;
}

// ── Multiselect Dropdown ──────────────────────────────────────────────────────
// A small react-bootstrap Dropdown with Form.Check checkboxes.
// selected = Set of selected values; all selected = show everything.
function MultiSelectDropdown({ label, options, selected, onChange }) {
   // selected === null means "all selected" (no filter active)
   const allSelected = selected === null || selected.size === options.length;

   const toggle = (val) => {
      const next = new Set(selected ?? options);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      // If all are selected, collapse back to null (no filter)
      onChange(next.size === options.length ? null : next);
   };

   const toggleAll = () => {
      if (allSelected) {
         // Deselect all — set to empty set (nothing passes filter = zero rows shown)
         onChange(new Set());
      } else {
         // Select all — clear filter
         onChange(null);
      }
   };

   const displayLabel = !allSelected && selected
      ? `${selected.size}/${options.length}`
      : label;

   return (
      <Dropdown>
         <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            id={`filter-${label}`}
            style={{ fontSize: 13 }}
         >
            {displayLabel}
         </Dropdown.Toggle>
         <Dropdown.Menu
            onClick={(e) => e.stopPropagation()}
            style={{ minWidth: 180, maxHeight: 260, overflowY: "auto" }}
         >
            <div className="px-3 py-1">
               <Form.Check
                  type="checkbox"
                  id={`ms-${label}-all`}
                  label="All"
                  checked={allSelected}
                  onChange={toggleAll}
                  style={{ fontSize: 13 }}
               />
            </div>
            <Dropdown.Divider />
            {options.map((opt) => (
               <div key={opt} className="px-3 py-1">
                  <Form.Check
                     type="checkbox"
                     id={`ms-${label}-${opt}`}
                     label={opt}
                     checked={allSelected || (selected != null && selected.has(opt))}
                     onChange={() => toggle(opt)}
                     style={{ fontSize: 13 }}
                  />
               </div>
            ))}
         </Dropdown.Menu>
      </Dropdown>
   );
}

// ── Column + page-size cog dropdown ──────────────────────────────────────────
function ColumnCogDropdown({ columns, visibleKeys, onChange, pageSize, onPageSizeChange }) {
   const move = (key, dir) => {
      const idx = visibleKeys.indexOf(key);
      if (idx === -1) return;
      const next = [...visibleKeys];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      onChange(next);
   };

   const toggleCol = (key) => {
      if (visibleKeys.includes(key)) {
         if (visibleKeys.length <= 1) return; // keep at least one
         onChange(visibleKeys.filter((k) => k !== key));
      } else {
         // Re-insert in definition order
         const defOrder = columns.map((c) => c.key);
         const next = defOrder.filter((k) => visibleKeys.includes(k) || k === key);
         onChange(next);
      }
   };

   return (
      <Dropdown>
         <Dropdown.Toggle
            as="span"
            id="report-col-cog"
            style={{ cursor: "pointer", color: "#6c757d" }}
         >
            <FaCog size={14} />
         </Dropdown.Toggle>
         <Dropdown.Menu
            onClick={(e) => e.stopPropagation()}
            style={{ minWidth: 210, maxHeight: 400, overflowY: "auto" }}
         >
            <Dropdown.Header style={{ fontSize: 12 }}>Rows per page</Dropdown.Header>
            {[10, 20, 50, 100].map((n) => (
               <div key={n} className="px-3 py-1">
                  <Form.Check
                     type="checkbox"
                     id={`page-size-${n}`}
                     label={String(n)}
                     checked={pageSize === n}
                     onChange={() => onPageSizeChange(n)}
                     style={{ fontSize: 13 }}
                  />
               </div>
            ))}
            <Dropdown.Divider />
            <Dropdown.Header style={{ fontSize: 12 }}>Columns (check to show)</Dropdown.Header>
            {columns.map((col) => {
               const visible = visibleKeys.includes(col.key);
               const idx = visibleKeys.indexOf(col.key);
               return (
                  <div
                     key={col.key}
                     className="d-flex align-items-center px-3 py-1 gap-2"
                     style={{ fontSize: 13 }}
                  >
                     <Form.Check
                        type="checkbox"
                        id={`col-vis-${col.key}`}
                        label={col.label}
                        checked={visible}
                        onChange={() => toggleCol(col.key)}
                        style={{ flex: 1 }}
                     />
                     {visible && (
                        <span className="d-flex gap-1">
                           <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              style={{ fontSize: 11, color: "#6c757d", lineHeight: 1 }}
                              onClick={() => move(col.key, -1)}
                              disabled={idx === 0}
                              title="Move left"
                           >
                              ◀
                           </button>
                           <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              style={{ fontSize: 11, color: "#6c757d", lineHeight: 1 }}
                              onClick={() => move(col.key, 1)}
                              disabled={idx === visibleKeys.length - 1}
                              title="Move right"
                           >
                              ▶
                           </button>
                        </span>
                     )}
                  </div>
               );
            })}
         </Dropdown.Menu>
      </Dropdown>
   );
}

// ── Sortable header cell ──────────────────────────────────────────────────────
const SORT_CARETS = { asc: " ▲", desc: " ▼", none: "" };

// ═════════════════════════════════════════════════════════════════════════════
export default function ReportRunner({ reportId }) {
   const def = reports[reportId];
   const { fetchData } = useFetchDatav2();

   const prior = subMonths(new Date(), 1);
   const [from] = useState(format(startOfMonth(prior), "yyyy-MM-dd"));
   const [to] = useState(format(endOfMonth(prior), "yyyy-MM-dd"));
   const [minVariance, setMinVariance] = useState("");
   const [envelope, setEnvelope] = useState(null);
   const [loading, setLoading] = useState(false);
   const [openRow, setOpenRow] = useState(null);

   // ── Filter state ────────────────────────────────────────────────────────
   // null = all selected (no filter); Set = restrict to these values
   const [filterLocations, setFilterLocations] = useState(null);
   const [filterSuppliers, setFilterSuppliers] = useState(null);
   const [filterCategories, setFilterCategories] = useState(null);

   // ── Group-by state ──────────────────────────────────────────────────────
   const [groupByField, setGroupByField] = useState(def.groupBy[0] ?? "supplier");

   // ── Sort state ──────────────────────────────────────────────────────────
   const [sortKey, setSortKey] = useState(null);
   const [sortDir, setSortDir] = useState("asc");

   // ── Column visibility + order ───────────────────────────────────────────
   const [visibleKeys, setVisibleKeys] = useState(() => def.columns.map((c) => c.key));

   // ── Pagination state ────────────────────────────────────────────────────
   const [pageIndex, setPageIndex] = useState(0);
   const [pageSize, setPageSize] = useState(50);

   const runLoad = (minVar) => {
      setLoading(true);
      // Same-origin relative path (like every other .sz call). Prefixing VITE_API_BASE_URL
      // (http://rekonsil.com) caused the HTTPS app to block the call as mixed content.
      const url = `${def.endpoint}?from=${from}&to=${to}&minVariance=${minVar}`;
      fetchData(url).then(({ responseData }) => {
         // Endpoint now streams a FLAT array of discrepancy rows (payload). Grouping,
         // summary, and totals are computed client-side below. Period label is derived here.
         const rows = Array.isArray(responseData?.payload) ? responseData.payload : [];
         setEnvelope({ rows, period: { label: format(prior, "MMM yyyy") } });
         setLoading(false);
      });
   };

   // eslint-disable-next-line react-hooks/exhaustive-deps
   useEffect(() => { runLoad(minVariance); }, []);

   // ── All rows from the flat payload ─────────────────────────────────────
   const allRows = useMemo(() => envelope?.rows ?? [], [envelope]);

   // ── Distinct filter options (memoized) ──────────────────────────────────
   const optLocations = useMemo(
      () => [...new Set(allRows.map((r) => r.location).filter(Boolean))].sort(),
      [allRows]
   );
   const optSuppliers = useMemo(
      () => [...new Set(allRows.map((r) => r.supplier).filter(Boolean))].sort(),
      [allRows]
   );
   const optCategories = useMemo(
      () => [...new Set(allRows.map((r) => r[def.categoryField]).filter(Boolean))].sort(),
      [allRows, def.categoryField]
   );

   // ── Apply filters ────────────────────────────────────────────────────────
   const filteredRows = useMemo(() => {
      const minV = minVariance === "" ? null : Number(minVariance);
      return allRows.filter((row) => {
         if (filterLocations  != null && !filterLocations.has(row.location)) return false;
         if (filterSuppliers  != null && !filterSuppliers.has(row.supplier)) return false;
         if (filterCategories != null && !filterCategories.has(row[def.categoryField])) return false;
         const minFieldVal = row[def.minField];
         if (minV != null && minFieldVal != null && Math.abs(minFieldVal) < minV) return false;
         return true;
      });
   }, [allRows, filterLocations, filterSuppliers, filterCategories, minVariance, def.categoryField, def.minField]);

   // ── Derived summary + totals from filtered rows ──────────────────────────
   const derivedSummary = useMemo(() => computeSummary(filteredRows, def.summary), [filteredRows, def.summary]);
   const visibleColObjects = useMemo(
      () => visibleKeys.map((k) => def.columns.find((c) => c.key === k)).filter(Boolean),
      [visibleKeys, def.columns]
   );
   const derivedTotals = useMemo(
      () => computeTotals(filteredRows, visibleColObjects),
      [filteredRows, visibleColObjects]
   );

   // ── Sorted flat list (for pagination slicing) ─────────────────────────────
   const sortedRows = useMemo(() => {
      if (!sortKey) return filteredRows;
      return [...filteredRows].sort(compareBy(sortKey, sortDir));
   }, [filteredRows, sortKey, sortDir]);

   // ── Pagination derived values ─────────────────────────────────────────────
   const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
   const clampedPage = Math.min(pageIndex, totalPages - 1);

   // ── Reset pageIndex when filters/sort/groupBy/pageSize change ────────────
   useEffect(() => { setPageIndex(0); }, [filterLocations, filterSuppliers, filterCategories, minVariance, groupByField, sortKey, sortDir, pageSize]);

   // ── Page-sliced rows → grouped for display ────────────────────────────────
   const pageRows = useMemo(
      () => sortedRows.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize),
      [sortedRows, clampedPage, pageSize]
   );

   // ── Group + sort (page slice only — for table body) ───────────────────────
   const derivedGroups = useMemo(
      () => groupRows(pageRows, groupByField, null /* already sorted */, sortDir, def.columns),
      [pageRows, groupByField, sortDir, def.columns]
   );

   // ── Sort toggle ──────────────────────────────────────────────────────────
   const handleSort = (key) => {
      if (sortKey === key) {
         if (sortDir === "asc") setSortDir("desc");
         else if (sortDir === "desc") { setSortKey(null); setSortDir("asc"); }
      } else {
         setSortKey(key);
         setSortDir("asc");
      }
   };

   // ── Styles ────────────────────────────────────────────────────────────────
   const th = { fontSize: 13, fontWeight: 600, color: "#000", textAlign: "left", cursor: "pointer", userSelect: "none" };
   const td = { fontSize: 14, fontWeight: 400, color: "#000" };
   const colSpanCount = visibleColObjects.length;

   const firstSumCol = visibleColObjects.find((c) => c.total === "sum");

   return (
      <div>
         {/* ── Title + Export ─────────────────────────────────────────────── */}
         <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 style={{ fontWeight: 600, margin: 0 }}>{def.title}</h5>
            <Button
               variant="outline-secondary"
               size="sm"
               disabled={!envelope}
               onClick={() =>
                  downloadCsv(
                     `${def.id}-${envelope.period.label}.csv`,
                     rowsToCsv(filteredRows, visibleColObjects)
                  )
               }
            >
               Export CSV
            </Button>
         </div>

         {/* ── Param / filter bar ────────────────────────────────────────── */}
         <div
            className="d-flex gap-3 align-items-end flex-wrap py-2 border-bottom"
            style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}
         >
            <div>
               <div style={{ fontSize: 13, color: "#6c757d" }}>Period</div>
               <div style={{ fontSize: 14 }}>
                  {envelope?.period?.label ?? format(prior, "MMM yyyy")}
               </div>
            </div>

            {optLocations.length > 0 && (
               <Form.Group>
                  <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                     Location
                  </Form.Label>
                  <div>
                     <MultiSelectDropdown
                        label="Location"
                        options={optLocations}
                        selected={filterLocations}
                        onChange={setFilterLocations}
                     />
                  </div>
               </Form.Group>
            )}

            {optSuppliers.length > 0 && (
               <Form.Group>
                  <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                     Supplier
                  </Form.Label>
                  <div>
                     <MultiSelectDropdown
                        label="Supplier"
                        options={optSuppliers}
                        selected={filterSuppliers}
                        onChange={setFilterSuppliers}
                     />
                  </div>
               </Form.Group>
            )}

            {optCategories.length > 0 && (
               <Form.Group>
                  <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                     {def.categoryLabel}
                  </Form.Label>
                  <div>
                     <MultiSelectDropdown
                        label={def.categoryLabel}
                        options={optCategories}
                        selected={filterCategories}
                        onChange={setFilterCategories}
                     />
                  </div>
               </Form.Group>
            )}

            <Form.Group>
               <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                  Min $
               </Form.Label>
               <Form.Control
                  size="sm"
                  type="number"
                  value={minVariance}
                  style={{ width: 110 }}
                  onChange={(e) => setMinVariance(e.target.value)}
               />
            </Form.Group>

            <Form.Group>
               <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                  Group by
               </Form.Label>
               <Form.Select
                  size="sm"
                  value={groupByField}
                  onChange={(e) => setGroupByField(e.target.value)}
                  style={{ width: 140 }}
               >
                  {(def.groupOptions ?? []).map((opt) => (
                     <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
               </Form.Select>
            </Form.Group>
         </div>

         {/* ── Summary strip (derived from filtered rows) ─────────────────── */}
         {envelope && <ReportSummaryStrip summary={derivedSummary} />}

         {/* ── Table toolbar (gear inline at table level) ────────────────── */}
         <div className="d-flex justify-content-end align-items-center mb-1" style={{ marginTop: 12 }}>
            <ColumnCogDropdown
               columns={def.columns}
               visibleKeys={visibleKeys}
               onChange={setVisibleKeys}
               pageSize={pageSize}
               onPageSizeChange={(n) => { setPageSize(n); setPageIndex(0); }}
            />
         </div>

         {/* ── Table ─────────────────────────────────────────────────────── */}
         <table className="table table-sm align-middle" style={{ marginTop: 0 }}>
            <thead>
               <tr>
                  {visibleColObjects.map((c) => {
                     const active = sortKey === c.key;
                     const caret = active ? SORT_CARETS[sortDir] : "";
                     return (
                        <th
                           key={c.key}
                           style={{ ...th, textAlign: c.align || "left" }}
                           onClick={() => handleSort(c.key)}
                        >
                           {c.label}
                           {caret && (
                              <span style={{ fontSize: 10, color: "#6c757d" }}>{caret}</span>
                           )}
                        </th>
                     );
                  })}
               </tr>
            </thead>
            <tbody>
               {loading && (
                  <tr>
                     <td colSpan={colSpanCount} style={td}>
                        Loading…
                     </td>
                  </tr>
               )}
               {!loading && envelope && filteredRows.length === 0 && (
                  <tr>
                     <td colSpan={colSpanCount} style={td}>
                        No rows match these filters.
                     </td>
                  </tr>
               )}
               {!loading &&
                  derivedGroups.map((g) => (
                     <React.Fragment key={g.key}>
                        <tr style={{ borderTop: "1px solid #dee2e6" }}>
                           <td
                              colSpan={colSpanCount}
                              style={{ ...td, fontWeight: 600 }}
                              data-group-header={g.key}
                           >
                              {g.label}{" "}
                              <span style={{ color: "#6c757d", fontWeight: 400 }}>
                                 · {g.rows.length} items
                                 {firstSumCol ? ` · ${money(g.subtotals[firstSumCol.key])}` : ""}
                              </span>
                           </td>
                        </tr>
                        {g.rows.map((row, i) => {
                           const rowId = `${g.key}-${i}`;
                           const drillCell = mkDrillCell(openRow, setOpenRow);
                           return (
                              <React.Fragment key={i}>
                                 <tr>
                                    {visibleColObjects.map((c) => (
                                       <td
                                          key={c.key}
                                          style={{ ...td, textAlign: c.align || "left" }}
                                       >
                                          {drillCell(c, row, rowId)}
                                       </td>
                                    ))}
                                 </tr>
                                 {openRow === rowId && (
                                    <tr>
                                       <td
                                          colSpan={colSpanCount}
                                          style={{ ...td, background: "#f8f9fa" }}
                                       >
                                          <div className="d-flex gap-4" style={{ fontSize: 13 }}>
                                             <div>
                                                <strong>Statement line</strong>
                                                <br />
                                                {row.invoice_no} · {row.statement_date} ·{" "}
                                                {money(Number(row.statement_amount))}
                                             </div>
                                             <div>
                                                <strong>ERP invoice</strong>
                                                <br />
                                                {row.invoice_no} · {row.invoice_date ?? "—"} ·{" "}
                                                {row.erp_amount != null
                                                   ? money(Number(row.erp_amount))
                                                   : "—"}
                                             </div>
                                             {row.drill?.doc_external_id && (
                                                <div>
                                                   <a
                                                      style={{ color: "#0073bb" }}
                                                      href={`/apps/Documents?doc=${row.drill.doc_external_id}`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                   >
                                                      View source document
                                                   </a>
                                                </div>
                                             )}
                                          </div>
                                       </td>
                                    </tr>
                                 )}
                              </React.Fragment>
                           );
                        })}
                     </React.Fragment>
                  ))}
            </tbody>
            {envelope && filteredRows.length > 0 && (
               <tfoot>
                  <tr style={{ borderTop: "2px solid #000", fontWeight: 600 }}>
                     {visibleColObjects.map((c) => (
                        <td
                           key={c.key}
                           style={{
                              ...td,
                              fontWeight: 600,
                              textAlign: c.align || "left",
                              fontVariantNumeric: "tabular-nums",
                           }}
                        >
                           {c.key === firstSumCol?.key
                              ? "Grand total"
                              : c.total === "sum"
                              ? money(derivedTotals[c.key])
                              : ""}
                        </td>
                     ))}
                  </tr>
               </tfoot>
            )}
         </table>

         {/* ── Pager footer ──────────────────────────────────────────────── */}
         {envelope && filteredRows.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
               <span style={{ fontSize: 13, color: "#6c757d" }}>
                  {filteredRows.length}{" "}
                  {def.id === "discrepancies"
                     ? (filteredRows.length === 1 ? "discrepancy" : "discrepancies")
                     : (filteredRows.length === 1 ? "row" : "rows")}
               </span>
               <div className="d-flex justify-content-center flex-grow-1">
                  <span style={{ fontSize: 13 }}>
                     Page <strong>{clampedPage + 1}</strong> of {totalPages}
                  </span>
               </div>
               <div className="d-flex gap-2">
                  <button
                     className="btn btn-primary btn-sm me-1"
                     onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                     disabled={clampedPage === 0}
                  >
                     Previous
                  </button>
                  <button
                     className="btn btn-primary btn-sm"
                     onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                     disabled={clampedPage >= totalPages - 1}
                  >
                     Next
                  </button>
               </div>
            </div>
         )}
      </div>
   );
}
